import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';

import GroupType from 'models/GroupType';

import { processTranslations } from './translations/helpers';

export type TranslateFnType = (text: string) => string;
export const defaultTranslate: TranslateFnType = (text) => text;

type TranslationContextType = {
  groupType?: GroupType;
  translate: TranslateFnType;
  translateAll: TranslateFnType;
};

const TranslationContext = createContext<TranslationContextType>({
  translate: defaultTranslate,
  translateAll: defaultTranslate,
});

interface TranslationProviderProps {
  groupType?: GroupType;
  children: ReactNode;
}

export function TranslationProvider({ groupType, children }: TranslationProviderProps) {
  const translations = useMemo(() => processTranslations(groupType?.translations ?? {}), [groupType?.translations]);

  const translate = useCallback(
    (text: string): string => {
      return translations[text.trim().toLowerCase()] || text;
    },
    [translations]
  );

  const translateAll = useCallback(
    (text: string) => {
      // Sort keys by length (longest first) to prioritize longer matches
      const sortedKeys = Object.keys(translations).sort((a, b) => b.length - a.length);

      const replacements: Array<{ placeholder: string; value: string }> = [];
      let result = text;

      // First pass: Replace all matches with unique placeholders
      sortedKeys.forEach((key) => {
        if (!translations[key]) return;

        const pattern = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`\\b${pattern}\\b`, 'gi');

        result = result.replace(regex, () => {
          const placeholder = `__TRANSLATION_${replacements.length}__`;
          replacements.push({ placeholder, value: translations[key] });
          return placeholder;
        });
      });

      // Second pass: Replace all placeholders with their actual values
      replacements.forEach(({ placeholder, value }) => {
        result = result.replace(placeholder, value);
      });

      return result;
    },
    [translations]
  );

  const value = useMemo(
    () => ({
      groupType,
      translate,
      translateAll,
    }),
    [groupType, translate, translateAll]
  );

  return <TranslationContext value={value}>{children}</TranslationContext>;
}

export function useTranslation() {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
}
